import { CircularProgress, TextField, Theme, useMediaQuery } from '@mui/material';
import { CheckedIcon } from 'assets/Icons';
import { Divider, EmptyCon, OrderButton, Spacer } from 'components/atoms';
import { Modal } from 'components/molecules';
import renderIcon from 'icon';
import moment from 'moment';
import QUERY_KEYS from 'network/config/queryKeys';
import { cancelOrder, getOrderTracking } from 'network/services/order';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { IoArrowBackCircleSharp } from 'react-icons/io5';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HeaderCON } from '../styles';
import { TrackHistoryCon } from './styles';

// Define the main flow steps
const MAIN_FLOW_STEPS = [
  { key: 'order_placed', label: 'Order Placed', icon: '📋' },
  { key: 'confirmed_for_delivery', label: 'Confirmed for Delivery', icon: '✅' },
  { key: 'in_transit', label: 'In Transit', icon: '🚚' },
  { key: 'delivered', label: 'Delivered', icon: '📦' }
];

// Status mapping for different scenarios
const STATUS_MAPPING: { [key: string]: string } = {
  order_placed: 'order_placed',
  confirmed_for_delivery: 'confirmed_for_delivery',
  in_transit: 'in_transit',
  delivered: 'delivered',
  completed: 'delivered', // Map completed to delivered
  cancelled: 'cancelled',
  failed: 'failed'
};

interface OrderStatusStep {
  key: string;
  label: string;
  icon: string;
}

// Get the progress through main flow for delivered/completed orders
function getMainFlowProgress(status: string): number {
  const normalizedStatus = STATUS_MAPPING[status.toLowerCase()];

  if (normalizedStatus === 'delivered') {
    return 3; // All main flow steps completed
  }

  const stepIndex = MAIN_FLOW_STEPS.findIndex(step => step.key === normalizedStatus);
  return stepIndex !== -1 ? stepIndex : 0;
}

// Check if order can be cancelled
function canCancelOrder(status: string): boolean {
  const normalizedStatus = STATUS_MAPPING[status.toLowerCase()];
  const nonCancellableStatuses = ['delivered', 'in_transit', 'cancelled', 'failed'];
  return !nonCancellableStatuses.includes(normalizedStatus);
}

// Get status display info
function getStatusDisplayInfo(status: string) {
  const normalizedStatus = STATUS_MAPPING[status.toLowerCase()];

  switch (normalizedStatus) {
    case 'delivered':
      return { color: '#10B981', label: 'Delivered' }; // Green
    case 'in_transit':
      return { color: '#3B82F6', label: 'In Transit' }; // Blue
    case 'cancelled':
      return { color: '#EF4444', label: 'Cancelled' }; // Red
    case 'failed':
      return { color: '#EF4444', label: 'Failed' }; // Red
    case 'confirmed_for_delivery':
      return { color: '#F59E0B', label: 'Confirmed for Delivery' }; // Amber
    default:
      return { color: '#6B7280', label: 'Order Placed' }; // Gray
  }
}

// Define the type inline to match the API response
type OrderTrackingResponse = {
  orderId: number;
  orderRef: string;
  orderDate: string;
  currentStatus: string;
  trackingNumber: string;
};

export const TrackHistory = () => {
  const { id } = useParams();
  const [reason, setReason] = React.useState('');
  const location = useLocation();
  const orderRef = location.state?.orderRef;
  const go = useNavigate();
  const handleBack = () => go(-1);
  const [isDecline, setDecline] = useState<boolean>(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const { data, isFetchedAfterMount, error } = useQuery({
    queryKey: [QUERY_KEYS.ORDER_TRACKING, id],
    queryFn: () => getOrderTracking(id ?? ''),
    enabled: !!id && !isNaN(Number(id)),
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchInterval: false,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  const { mutateAsync: cancel } = useMutation({
    mutationFn: cancelOrder,
    onSuccess: () => {
      enqueueSnackbar(`Order ${id} cancelled successfully.`, {
        variant: 'success'
      });
      setDecline(false);
    },
    onError: () => {
      enqueueSnackbar(`Failed to cancel order #${id}`, { variant: 'error' });
      setDecline(false);
    }
  });

  const handleCancel = () => {
    cancel({ id: Number(id), reason: reason });
  };

  // Extract order data
  const orderData: OrderTrackingResponse | null = data ? (data as unknown as OrderTrackingResponse) : null;
  const currentStatus = orderData?.currentStatus || '';
  const normalizedStatus = STATUS_MAPPING[currentStatus.toLowerCase()];
  const statusInfo = getStatusDisplayInfo(currentStatus);
  const canCancel = canCancelOrder(currentStatus);

  // For cancelled/failed orders, show single status
  const isCancelledOrFailed = ['cancelled', 'failed'].includes(normalizedStatus);

  // For main flow, get current progress
  const mainFlowProgress = getMainFlowProgress(currentStatus);

  const renderLinearTrackingStepper = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          maxWidth: 400,
          margin: '0 auto',
          padding: '0 16px'
        }}>
        {MAIN_FLOW_STEPS.map((step: OrderStatusStep, idx: number) => {
          const isCompleted = idx <= mainFlowProgress;
          const isActive = idx === mainFlowProgress && normalizedStatus !== 'delivered';
          const isLastStep = idx === MAIN_FLOW_STEPS.length - 1;

          return (
            <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start' }}>
              {/* Timeline Line and Icon */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginRight: 16,
                  minWidth: 40
                }}>
                {/* Status Icon */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? '#10B981' : isActive ? '#3B82F6' : '#E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${isCompleted ? '#10B981' : isActive ? '#3B82F6' : '#E5E7EB'}`,
                    fontSize: 16,
                    color: isCompleted || isActive ? 'white' : '#9CA3AF',
                    fontWeight: 'bold',
                    transition: 'all 0.3s ease'
                  }}>
                  {isCompleted ? (
                    <CheckedIcon style={{ width: 20, height: 20 }} />
                  ) : (
                    <span style={{ fontSize: 18 }}>{step.icon}</span>
                  )}
                </div>

                {/* Connecting Line */}
                {!isLastStep && (
                  <div
                    style={{
                      width: 2,
                      height: 40,
                      backgroundColor: isCompleted ? '#10B981' : '#E5E7EB',
                      marginTop: 8,
                      marginBottom: 8,
                      transition: 'all 0.3s ease'
                    }}
                  />
                )}
              </div>

              {/* Step Content */}
              <div
                style={{
                  flex: 1,
                  paddingBottom: isLastStep ? 0 : 24,
                  minHeight: isLastStep ? 'auto' : 64
                }}>
                <div
                  style={{
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isCompleted ? '#10B981' : isActive ? '#3B82F6' : '#6B7280',
                    fontSize: 16,
                    marginBottom: 4,
                    transition: 'all 0.3s ease'
                  }}>
                  {step.label}
                </div>

                {/* Step Details */}
                <div style={{ fontSize: 14, color: '#9CA3AF' }}>
                  {isCompleted && orderData && (
                    <div>
                      {idx === 0 && (
                        <span>Order placed on {moment(orderData.orderDate).format('DD MMM YYYY, h:mma')}</span>
                      )}
                      {idx === 1 && isCompleted && <span>Your order has been confirmed and is being prepared</span>}
                      {idx === 2 && isCompleted && <span>Your order is on its way to you</span>}
                      {idx === 3 && isCompleted && (
                        <span>
                          Successfully delivered on {moment(orderData.orderDate).format('DD MMM YYYY, h:mma')}
                        </span>
                      )}
                    </div>
                  )}
                  {isActive && !isCompleted && (
                    <div>
                      {idx === 0 && <span>Processing your order...</span>}
                      {idx === 1 && <span>Preparing your order for delivery...</span>}
                      {idx === 2 && <span>Your order is currently in transit...</span>}
                      {idx === 3 && <span>Delivering your order...</span>}
                    </div>
                  )}
                  {!isCompleted && !isActive && (
                    <div>
                      <span>Pending on staging</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCancelledOrFailedStatus = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '32px 16px',
          textAlign: 'center',
          maxWidth: 400,
          margin: '0 auto'
        }}>
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: statusInfo.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
            fontSize: 32
          }}>
          {normalizedStatus === 'cancelled' ? '❌' : '⚠️'}
        </div>

        <h3
          style={{
            color: statusInfo.color,
            fontSize: 24,
            fontWeight: 'bold',
            margin: '0 0 8px 0'
          }}>
          Order {statusInfo.label}
        </h3>

        {orderData && (
          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>
            <div>Order Ref: {orderData.orderRef}</div>
            <div>Date: {moment(orderData.orderDate).format('DD MMM YYYY, h:mma')}</div>
          </div>
        )}

        <div
          style={{
            backgroundColor: '#F3F4F6',
            borderRadius: 8,
            padding: 16,
            fontSize: 14,
            color: '#6B7280',
            textAlign: 'center'
          }}>
          {normalizedStatus === 'cancelled'
            ? 'This order has been cancelled and will not be delivered.'
            : 'This order has failed and could not be processed.'}
        </div>
      </div>
    );
  };

  return (
    <>
      <HeaderCON>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {renderIcon(IoArrowBackCircleSharp, {
            size: 24,
            className: 'text-black flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors',
            onClick: handleBack
          })}
          <span>Delivery updates</span>
        </div>
      </HeaderCON>

      {/* Handle invalid id and error */}
      {(!id || isNaN(Number(id))) && <EmptyCon>Invalid order ID</EmptyCon>}
      {error && <EmptyCon>Failed to load tracking details</EmptyCon>}

      {!isFetchedAfterMount && (
        <EmptyCon>
          <CircularProgress size={30} />
        </EmptyCon>
      )}

      {isFetchedAfterMount && orderData && (
        <TrackHistoryCon>
          <div className="cancel">
            <div>
              <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 4 }}>Order #{orderData.orderRef}</div>
              <div
                className={normalizedStatus}
                style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  color: statusInfo.color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: statusInfo.color
                  }}
                />
                {statusInfo.label}
              </div>
            </div>

            {/* Show Cancel Order button only if order can be cancelled */}
            {canCancel && (
              <OrderButton onClick={() => setDecline(true)} outlined color="#EF4444">
                Cancel Order
              </OrderButton>
            )}
          </div>

          <Divider />
          <Spacer height={32} />

          {/* Show appropriate tracking visualization */}
          {isCancelledOrFailed ? renderCancelledOrFailedStatus() : renderLinearTrackingStepper()}

          <Spacer height={32} />

          {/* Order Details Card */}
          {!isCancelledOrFailed && orderData && (
            <div
              style={{
                backgroundColor: '#F9FAFB',
                borderRadius: 12,
                padding: 20,
                margin: '16px 0',
                border: '1px solid #E5E7EB'
              }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 'bold' }}>Order Details</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280' }}>Order ID:</span>
                  <span style={{ fontWeight: 'bold' }}>{orderData.orderId}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280' }}>Reference:</span>
                  <span style={{ fontWeight: 'bold' }}>{orderData.orderRef}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280' }}>Tracking Number:</span>
                  <span style={{ fontWeight: 'bold' }}>{orderData.trackingNumber}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280' }}>Order Date:</span>
                  <span style={{ fontWeight: 'bold' }}>{moment(orderData.orderDate).format('DD MMM YYYY, h:mma')}</span>
                </div>
              </div>
            </div>
          )}

          <Spacer height={32} />

          {/* Cancel Order Modal */}
          {isDecline && (
            <Modal maxWidth="400px" onModalClose={() => setDecline(false)}>
              <div className="flex items-center w-full flex-col gap-5">
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 'bold' }}>Cancel Order</h3>
                </div>

                <p style={{ textAlign: 'center', color: '#6B7280', margin: 0 }}>
                  Are you sure you want to cancel your order? This action cannot be undone.
                </p>

                <TextField
                  rows={4}
                  multiline
                  fullWidth
                  value={reason}
                  onChange={e => setReason(e.currentTarget.value)}
                  placeholder="Please tell us why you're cancelling this order"
                  style={{ marginTop: 8 }}
                />

                <div className="flex items-center gap-3 w-full">
                  <OrderButton onClick={() => setDecline(false)} outlined color="#6B7280" style={{ flex: 1 }}>
                    Keep Order
                  </OrderButton>
                  <OrderButton
                    className="disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:opacity-30"
                    onClick={handleCancel}
                    outlined
                    color="#EF4444"
                    disabled={reason.length < 3}
                    style={{ flex: 1 }}>
                    Cancel Order
                  </OrderButton>
                </div>
              </div>
            </Modal>
          )}
        </TrackHistoryCon>
      )}
    </>
  );
};
